import { contactEmail, SiteFooter } from "./SiteFooter"

// The privacy policy and terms of use. Both are plain documents with no data
// of their own, so they render the same way signed in or signed out. App
// routes them before the auth gate.

const lastUpdated = "21 August 2026"

interface LegalProps {
	kind: "privacy" | "terms"
}

export function Legal({ kind }: LegalProps) {
	return (
		<div class="h-100 overflow-auto">
			<div class="container page" style="max-width: 40rem;">
				{kind === "privacy" ? <Privacy /> : <Terms />}
				<hr />
				<p class="muted small text-center">Last updated: {lastUpdated}.</p>
				<SiteFooter />
			</div>
		</div>
	)
}

function Privacy() {
	return (
		<div class="stack gap-3">
			<h1>Privacy policy</h1>
			<p>
				Quizfish is a trivia game run as a personal project by{" "}
				<a href="https://sharats.me">Shri</a>. This page describes what it
				stores about you and what it does with that data.
			</p>

			<h2>What we store</h2>
			<ul>
				<li>
					<strong>Your account:</strong> email address, username, and a bcrypt
					hash of your password. The password itself is never stored.
				</li>
				<li>
					<strong>Your games:</strong> the rounds you play, the answers you
					pick, your scores, and how long you took to answer in a match.
				</li>
				<li>
					<strong>Your friends:</strong> friend requests you send and receive,
					and who invited you, if you joined from an invite.
				</li>
				<li>
					<strong>Invites you send:</strong> the email address you invite and
					the status of the invitation, so the same invite cannot be used twice.
				</li>
				<li>
					<strong>Sessions:</strong> a random token identifying your logged-in
					browser.
				</li>
				<li>
					<strong>Email verification:</strong> a random token emailed to your
					address when you sign up, and the time you verified it. Until you do,
					the account cannot do anything.
				</li>
			</ul>
			<p>
				There is no analytics, advertising, tracking pixels, or third-party
				scripts. Nothing is sold or shared for marketing.
			</p>

			<h2>Cookies</h2>
			<p>
				One cookie, <code>qf_session</code>, keeps you logged in for 30 days and
				is renewed as you use the site. It is required for the game to work.
				There are no other cookies.
			</p>

			<h2>Who else sees it</h2>
			<ul>
				<li>
					<strong>Other players.</strong> Your username is visible to anyone you
					play or exchange friend requests with, and your invitations go out
					under your username. Your profile statistics are private unless you
					turn on the public profile setting. Your email address is never shown
					to other players.
				</li>
				<li>
					<strong>Resend</strong> (resend.com) delivers invitation emails, so it
					processes the recipient's email address and the contents of the
					message.
				</li>
				<li>
					<strong>Dodo Payments</strong> (dodopayments.com) is the merchant of
					record for Pro subscriptions: it takes the payment, holds your card
					details and billing address, and issues the invoice. We never see your
					card. What we keep is your subscription's status and dates and Dodo's
					reference for you, so that the game knows you're on Pro and can send
					you to Dodo's page to manage it.
				</li>
				<li>
					<strong>The hosting and database providers</strong> the service runs
					on.
				</li>
			</ul>
			<p>Data may also be disclosed if the law requires it.</p>

			<h2>Keeping and deleting data</h2>
			<p>
				Your account and game history are kept for as long as your account
				exists. Email <a href={`mailto:${contactEmail}`}>{contactEmail}</a> to
				get a copy of your data or to have your account and its data deleted.
			</p>

			<h2>Children</h2>
			<p>
				Some topics are aimed at young children, but accounts are meant for
				adults. A child should play on a parent's account rather than have one
				of their own. Signing up needs a working email address, which is the
				only check made before an account is created.
			</p>

			<h2>Security</h2>
			<p>
				Passwords are hashed, sessions expire, and traffic is served over HTTPS.
				This is a personal project and cannot promise perfect security, so
				please do not reuse an important password here.
			</p>

			<h2>Changes</h2>
			<p>If this policy changes, the date below will change with it.</p>

			<h2>Contact</h2>
			<p>
				Questions go to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
			</p>
		</div>
	)
}

function Terms() {
	return (
		<div class="stack gap-3">
			<h1>Terms of use</h1>
			<p>
				Quizfish is a trivia game run as a personal project by{" "}
				<a href="https://sharats.me">Shri</a>. By using it you agree to the
				following.
			</p>

			<h2>Your account</h2>
			<p>
				Accounts are personal: one account per person, for your own
				non-commercial use, at an email address that is yours. Keep your
				password to yourself. You are responsible for activity under your
				account. Let us know if you think someone else is using it.
			</p>

			<h2>Playing fair</h2>
			<ul>
				<li>
					Do not harass other players or choose a username meant to offend.
				</li>
				<li>Only invite people who want an invitation.</li>
				<li>
					Play through the site as it is offered. Do not script the game, send
					automated requests to the API, or try to read answers before they are
					revealed.
				</li>
				<li>Do not attempt to break into other accounts or the service.</li>
			</ul>

			<h2>The questions</h2>
			<p>
				Questions are generated automatically from small built-in datasets. They
				can be wrong, out of date, or awkwardly worded. Quizfish is a game and
				is not a reliable reference.
			</p>

			<h2>Pro</h2>
			<p>
				The free plan allows a set number of rounds a day. Pro removes the cap
				for a monthly subscription, charged in advance by Dodo Payments, which
				is the seller of record. You can cancel at any time from the
				subscription page on your profile; Pro then lasts to the end of the
				period you've paid for and is not renewed. If a renewal payment fails
				you go back to the free plan until the payment goes through. Refunds are
				given for a period that was charged in error, and otherwise at our
				discretion; email <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
				to ask. The price may change, and you'll be told before a change applies
				to you.
			</p>

			<h2>Availability</h2>
			<p>
				This is a personal project offered as-is, with no warranty of any kind
				and no promise that it will stay available or that your game history
				will be kept. It may change or shut down at any time. To the extent the
				law allows, there is no liability for any loss arising from its use.
			</p>

			<h2>Closing an account</h2>
			<p>
				You can ask for your account to be deleted at any time (see the{" "}
				<a href="/privacy">privacy policy</a>). Accounts that break these terms
				may be suspended or removed, along with the invites they issued.
			</p>

			<h2>Third-party material</h2>
			<p>
				Flag images come from the flag-icons project, country silhouettes from
				mapsicon, and the interface uses the Inter typeface. Each remains under
				its own licence.
			</p>

			<h2>Changes</h2>
			<p>
				These terms may change. The date below says when they last did.
				Continuing to play after a change means you accept it.
			</p>

			<h2>Contact</h2>
			<p>
				Questions go to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
			</p>
		</div>
	)
}
